import { useRef, type ChangeEvent } from 'react';
import { ImagePlus, FileText, Link as LinkIcon } from 'lucide-react';
import { SUPPORTED_IMAGE_MIME_TYPES, SUPPORTED_DOCUMENT_MIME_TYPES } from '@/api-types';
import type { ContextIntegrationProvider } from '@/api-types';

export interface AttachmentUploadButtonProps {
	onFilesSelected: (files: File[]) => void;
	onUrlClick?: () => void;
	onIntegrationClick?: (provider: ContextIntegrationProvider) => void;
	connectedIntegrations?: ContextIntegrationProvider[];
	availableProviders?: ContextIntegrationProvider[];
	disabled?: boolean;
	compact?: boolean;
	className?: string;
}

/**
 * Row of pill-shaped context buttons for adding attachments and integrations.
 * Non-compact (home page): icon + text label pills.
 * Compact (chat input): icon-only small buttons.
 */
export function AttachmentUploadButton({
	onFilesSelected,
	onUrlClick,
	onIntegrationClick,
	connectedIntegrations = [],
	availableProviders,
	disabled = false,
	compact = false,
	className = '',
}: AttachmentUploadButtonProps) {
	const imageInputRef = useRef<HTMLInputElement>(null);
	const docInputRef = useRef<HTMLInputElement>(null);

	const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(e.target.files || []);
		if (files.length > 0) {
			onFilesSelected(files);
		}
		if (e.target) {
			e.target.value = '';
		}
	};

	if (compact) {
		const iconSize = 'size-3.5';
		const btnClass = 'p-1 rounded-md bg-transparent hover:bg-bg-3 text-text-secondary hover:text-text-primary transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed';

		return (
			<div className={`flex items-center gap-0.5 ${className}`}>
				<input ref={imageInputRef} type="file" accept={SUPPORTED_IMAGE_MIME_TYPES.join(',')} multiple onChange={handleFileChange} className="hidden" disabled={disabled} />
				<input ref={docInputRef} type="file" accept={SUPPORTED_DOCUMENT_MIME_TYPES.join(',')} multiple onChange={handleFileChange} className="hidden" disabled={disabled} />
				<button type="button" onClick={() => imageInputRef.current?.click()} disabled={disabled} className={btnClass} aria-label="Upload image" title="Upload image">
					<ImagePlus className={iconSize} strokeWidth={1.5} />
				</button>
				<button type="button" onClick={() => docInputRef.current?.click()} disabled={disabled} className={btnClass} aria-label="Upload document" title="Upload document">
					<FileText className={iconSize} strokeWidth={1.5} />
				</button>
				{onUrlClick && (
					<button type="button" onClick={onUrlClick} disabled={disabled} className={btnClass} aria-label="Add URL" title="Add URL">
						<LinkIcon className={iconSize} strokeWidth={1.5} />
					</button>
				)}
			</div>
		);
	}

	const pillClass = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-border-primary bg-bg-3 hover:bg-bg-2 text-text-secondary hover:text-text-primary transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed';

	return (
		<div className={`flex items-center gap-2 flex-wrap ${className}`}>
			<input ref={imageInputRef} type="file" accept={SUPPORTED_IMAGE_MIME_TYPES.join(',')} multiple onChange={handleFileChange} className="hidden" disabled={disabled} />
			<input ref={docInputRef} type="file" accept={SUPPORTED_DOCUMENT_MIME_TYPES.join(',')} multiple onChange={handleFileChange} className="hidden" disabled={disabled} />

			<button type="button" onClick={() => imageInputRef.current?.click()} disabled={disabled} className={pillClass}>
				<ImagePlus className="size-3.5" strokeWidth={1.5} />
				Image
			</button>

			<button type="button" onClick={() => docInputRef.current?.click()} disabled={disabled} className={pillClass}>
				<FileText className="size-3.5" strokeWidth={1.5} />
				Document
			</button>

			{onUrlClick && (
				<button type="button" onClick={onUrlClick} disabled={disabled} className={pillClass}>
					<LinkIcon className="size-3.5" strokeWidth={1.5} />
					URL
				</button>
			)}

			{onIntegrationClick && INTEGRATION_OPTIONS
				.filter(({ id }) => !availableProviders || availableProviders.includes(id))
				.map(({ id, label, icon: Icon }) => (
				<button
					key={id}
					type="button"
					onClick={() => onIntegrationClick(id)}
					disabled={disabled}
					className={pillClass}
					title={connectedIntegrations.includes(id)
						? `Browse ${label} content`
						: `Connect ${label} in org settings`}
				>
					<Icon className="size-3.5" />
					{label}
				</button>
			))}
		</div>
	);
}

const INTEGRATION_OPTIONS: { id: ContextIntegrationProvider; label: string; icon: React.FC<{ className?: string }> }[] = [
	{
		id: 'notion',
		label: 'Notion',
		icon: ({ className }) => (
			<svg className={className} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
				<path d="M6.017 4.313l55.333-4.087c6.797-.583 8.543-.19 12.817 2.917l17.663 12.443c2.913 2.14 3.883 2.723 3.883 5.053v68.243c0 4.277-1.553 6.807-6.99 7.193L24.467 99.967c-4.08.193-6.023-.39-8.16-3.113L3.3 79.94c-2.333-3.113-3.3-5.443-3.3-8.167V11.113c0-3.497 1.553-6.413 6.017-6.8z" fill="currentColor"/>
				<path d="M61.35.227l-55.333 4.087C1.553 4.7 0 7.617 0 11.113v60.66c0 2.723.967 5.053 3.3 8.167l12.993 16.913c2.137 2.723 4.08 3.307 8.16 3.113l64.257-3.89c5.433-.387 6.99-2.917 6.99-7.193V20.64c0-2.21-.82-2.873-3.303-4.673l-.58-.38-17.663-12.443C70.893.14 69.147-.443 62.35.14l-1 .087z" fill="#fff"/>
				<path fillRule="evenodd" clipRule="evenodd" d="M25.723 19.793c-5.077.527-6.243.643-9.15-1.53l-7.093-5.25c-.777-.583-.39-1.36 1.36-1.553l52.237-3.887c4.473-.39 6.8 1.167 8.543 2.527l8.157 5.833c.39.197.97 1.17.193 1.17l-54.05 3.107-.197-.417z" fill="currentColor"/>
				<path d="M19.457 88.533V35.4c0-2.527.777-3.693 3.107-3.887l58.523-3.5c2.14-.193 3.107 1.167 3.107 3.693v52.743c0 2.527-1.36 5.443-5.057 5.64l-53.067 3.11c-3.69.193-6.613-1.167-6.613-4.667z" fill="#fff"/>
				<path d="M63.037 39.867c.39 1.75 0 3.5-1.75 3.697l-2.723.58v39.203c-2.333 1.167-4.473 1.753-6.22 1.753-2.917 0-3.693-.97-5.83-3.5L30.607 55.94V79.94l5.64 1.36s0 3.5-4.86 3.5l-13.383.777c-.39-.777 0-2.723 1.36-3.11l3.497-.97V47.647l-4.86-.39c-.39-1.75.583-4.277 3.3-4.473l14.357-.97 16.8 25.667V45.507l-4.667-.583c-.39-2.14 1.167-3.693 3.11-3.887l14.137-.777v-.393z" fill="currentColor"/>
			</svg>
		),
	},
	{
		id: 'linear',
		label: 'Linear',
		icon: ({ className }) => (
			<svg className={className} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
				<path d="M1.227 61.458a51.04 51.04 0 0 1-1.12-6.14l44.576 44.575a51.04 51.04 0 0 1-6.14-1.12L1.227 61.459zM.003 51.859a50.492 50.492 0 0 1 1.053-8.334l57.278 57.278a50.5 50.5 0 0 1-8.334 1.053L.003 51.859zM3.19 39.605a49.925 49.925 0 0 1 2.565-6.385l61.025 61.025a49.923 49.923 0 0 1-6.385 2.565L3.19 39.605zM8.293 29.95a50.142 50.142 0 0 1 3.97-5.616L79.666 91.738a50.146 50.146 0 0 1-5.616 3.97L8.293 29.948zM16.124 21.193a50.346 50.346 0 0 1 5.176-4.712L87.52 82.701a50.35 50.35 0 0 1-4.712 5.176L16.124 21.193zM25.07 13.71a50.144 50.144 0 0 1 5.617-3.97l65.756 65.757a50.144 50.144 0 0 1-3.97 5.616L25.07 13.71zM33.22 8.607a49.925 49.925 0 0 1 6.385-2.565l55.204 55.204a49.926 49.926 0 0 1-2.565 6.385L33.22 8.607zM43.525 4.247a50.493 50.493 0 0 1 8.334-1.053l45.997 45.997a50.5 50.5 0 0 1-1.053 8.334L43.525 4.247zM55.317 2.014a51.04 51.04 0 0 1 6.14 1.12l37.316 37.316a51.04 51.04 0 0 1-1.12-6.14L55.317 2.014zM65.908 4.866a50.18 50.18 0 0 1 5.37 2.82l24.037 24.036a50.18 50.18 0 0 1 2.82 5.37L65.907 4.866zM75.79 10.57a50.09 50.09 0 0 1 4.271 3.37l10 10a50.094 50.094 0 0 1 3.37 4.27L75.79 10.57zM83.893 17.675a50.18 50.18 0 0 1 2.82 5.37l5.423 5.421a50.183 50.183 0 0 1-2.82-5.37l-5.423-5.42z" fill="currentColor"/>
			</svg>
		),
	},
	{
		id: 'google_drive',
		label: 'Google Drive',
		icon: ({ className }) => (
			<svg className={className} viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
				<path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/>
				<path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-20.4 35.3c-.8 1.4-1.2 2.95-1.2 4.5h27.5z" fill="#00ac47"/>
				<path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335"/>
				<path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/>
				<path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc"/>
				<path d="m73.4 26.5-10.2-17.65c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 23.8h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00"/>
			</svg>
		),
	},
];
